import React, { useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import { useDispatch, useSelector } from 'react-redux'
import { Navigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
  confirmTwoFactorSetupRoutine,
  getTwoFactorStatusRoutine,
  startTwoFactorSetupRoutine
} from 'features/auth/ducks/actions'
import {
  isUserLoggedIn,
  getCurrentUser,
  selectTwoFactorSetup,
  selectTwoFactorStatus
} from 'features/auth/ducks/selectors'
import * as authService from 'services/AuthService'
import { PATHS, redirect } from 'utils/paths'
import { homePathFor, takeStoredRedirect } from 'utils/postLoginDestination'

const CODE_LENGTH = 6
const SKIP_OPTIONS = [
  { label: 'Remind me in 1 week', weeks: 1 },
  { label: 'Remind me in 2 weeks', weeks: 2 },
  { label: 'Remind me in 4 weeks', weeks: 4 }
]

const TwoFactorSetup = () => {
  const dispatch = useDispatch()
  const isLoggedIn = useSelector(isUserLoggedIn)
  const currentUser = useSelector(getCurrentUser)
  const status = useSelector(selectTwoFactorStatus)
  const setup = useSelector(selectTwoFactorSetup)
  const [step, setStep] = useState('intro')
  const [code, setCode] = useState('')
  const [setupSubmitted, setSetupSubmitted] = useState(false)
  const [showManualSetup, setShowManualSetup] = useState(false)
  const [copiedRecoveryCodes, setCopiedRecoveryCodes] = useState(false)
  const codeInputRefs = useRef([])

  const isTwoFactorEnabled = status?.enabled ?? currentUser.twoFactorEnabled
  const recoveryCodes = useMemo(() => setup?.recoveryCodes || [], [setup])

  useEffect(() => {
    if (currentUser.twoFactorEnabled) {
      dispatch(getTwoFactorStatusRoutine())
    }
  }, [dispatch, currentUser.twoFactorEnabled])

  useEffect(() => {
    if (!currentUser.twoFactorEnabled && step === 'setup' && !setup) {
      dispatch(startTwoFactorSetupRoutine())
    }
  }, [dispatch, currentUser.twoFactorEnabled, setup, step])

  useEffect(() => {
    if (setupSubmitted && status?.enabled && recoveryCodes.length > 0) {
      setStep('recovery')
      setSetupSubmitted(false)
    }
  }, [recoveryCodes.length, setupSubmitted, status?.enabled])

  useEffect(() => {
    if (isTwoFactorEnabled && step !== 'setup' && step !== 'recovery') {
      setStep('enabled')
    }
  }, [isTwoFactorEnabled, step])

  if (!isLoggedIn) {
    return <Navigate to={PATHS.login} replace />
  }

  const getDefaultDestination = () => takeStoredRedirect() || homePathFor(currentUser)

  const continueToApp = () => {
    redirect(getDefaultDestination())
  }

  const handleSkip = async weeks => {
    try {
      await authService.skipTwoFactorSetupPrompt({ weeks })
      continueToApp()
    } catch {
      toast.error('Two-factor reminder preference could not be saved.')
    }
  }

  const handleStartSetup = () => {
    setStep('setup')
  }

  const handleConfirm = e => {
    e.preventDefault()

    if (code.length !== CODE_LENGTH) return

    setSetupSubmitted(true)
    dispatch(confirmTwoFactorSetupRoutine({ code }))
  }

  const focusCodeInput = index => {
    codeInputRefs.current[index]?.focus()
  }

  const updateCode = (nextCode, focusIndex) => {
    const sanitizedCode = nextCode.replace(/\D/g, '').slice(0, CODE_LENGTH)
    setCode(sanitizedCode)

    if (Number.isInteger(focusIndex)) {
      setTimeout(() => focusCodeInput(Math.max(0, Math.min(focusIndex, CODE_LENGTH - 1))), 0)
    }
  }

  const handleCodeChange = index => event => {
    const value = event.target.value.replace(/\D/g, '')

    if (value.length > 1) {
      const nextCode = `${code.slice(0, index)}${value}${code.slice(index + value.length)}`
      updateCode(nextCode, Math.min(index + value.length, CODE_LENGTH - 1))
      return
    }

    const codeCharacters = code.padEnd(CODE_LENGTH, ' ').split('')
    codeCharacters[index] = value || ' '
    updateCode(codeCharacters.join('').replace(/\s/g, ''), value ? index + 1 : index)
  }

  const handleCodeKeyDown = index => event => {
    if (event.key === 'Backspace' && !code[index] && index > 0) {
      event.preventDefault()
      const codeCharacters = code.padEnd(CODE_LENGTH, ' ').split('')
      codeCharacters[index - 1] = ' '
      updateCode(codeCharacters.join('').replace(/\s/g, ''), index - 1)
    }

    if (event.key === 'ArrowLeft' && index > 0) {
      event.preventDefault()
      focusCodeInput(index - 1)
    }

    if (event.key === 'ArrowRight' && index < CODE_LENGTH - 1) {
      event.preventDefault()
      focusCodeInput(index + 1)
    }
  }

  const handleCodePaste = index => event => {
    event.preventDefault()
    const pastedCode = event.clipboardData.getData('text')
    const nextCode = `${code.slice(0, index)}${pastedCode}${code.slice(index + pastedCode.length)}`
    updateCode(nextCode, CODE_LENGTH - 1)
  }

  const handleDownloadRecoveryCodes = () => {
    if (!recoveryCodes.length) return

    const content = [
      'Building Passport recovery codes',
      `Generated: ${new Date().toISOString()}`,
      '',
      ...recoveryCodes
    ].join('\n')

    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'building-passport-recovery-codes.txt'
    document.body.appendChild(link)
    link.click()
    link.remove()
    URL.revokeObjectURL(url)
  }

  const handleCopyRecoveryCodes = async () => {
    if (!recoveryCodes.length || !navigator.clipboard) {
      toast.error('Recovery codes could not be copied.')
      return
    }

    try {
      await navigator.clipboard.writeText(recoveryCodes.join('\n'))
      setCopiedRecoveryCodes(true)
      toast.success('Recovery codes copied.')
      setTimeout(() => setCopiedRecoveryCodes(false), 2500)
    } catch {
      toast.error('Recovery codes could not be copied.')
    }
  }

  return (
    <PageWrapper>
      <Panel>
        {step === 'intro' && (
          <>
            <Eyebrow>Account security</Eyebrow>
            <Title>Building Passport now supports two-factor authentication</Title>
            <Description>
              Would you like to set this up now? You can use Google Authenticator or Microsoft Authenticator to add an extra layer of protection to your account.
            </Description>
            <PrimaryButton type='button' onClick={handleStartSetup} data-tour-id='twoFactor.start-btn'>
              Set up two-factor authentication
            </PrimaryButton>
            <SkipSection>
              <SkipTitle>Not right now?</SkipTitle>
              <SkipDescription>
                Choose when Building Passport should remind you again on this browser.
              </SkipDescription>
              <SkipGrid>
                {SKIP_OPTIONS.map(option => (
                  <SecondaryButton
                    key={option.weeks}
                    type='button'
                    onClick={() => handleSkip(option.weeks)}
                  >
                    {option.label}
                  </SecondaryButton>
                ))}
                <SecondaryButton type='button' onClick={() => handleSkip(null)}>
                  Ask me every sign-in
                </SecondaryButton>
              </SkipGrid>
            </SkipSection>
          </>
        )}

        {step === 'setup' && (
          <>
            <Eyebrow>Account security</Eyebrow>
            <Title>Two-factor authentication</Title>
            <Description>
              Use Google Authenticator or Microsoft Authenticator to generate a secure login code for this account.
            </Description>

            {!setup?.qrCodeSvg && (
              <LoadingBox>Preparing your authenticator setup...</LoadingBox>
            )}

            {setup?.qrCodeSvg && (
              <QrWrapper dangerouslySetInnerHTML={{ __html: setup.qrCodeSvg }} data-tour-id='twoFactor.qr' />
            )}

            <Steps>
              <li>Open Google Authenticator or Microsoft Authenticator.</li>
              <li>Scan the QR code from within the app.</li>
              <li>Enter the 6-digit code below to finish setup.</li>
            </Steps>

            <ManualSetupButton type='button' onClick={() => setShowManualSetup(value => !value)}>
              {showManualSetup ? 'Hide manual setup key' : 'Set up manually'}
            </ManualSetupButton>

            {showManualSetup && setup?.secret && (
              <ManualKey>
                <span>Manual setup key</span>
                <code>{setup.secret}</code>
              </ManualKey>
            )}

            <SetupForm onSubmit={handleConfirm}>
              <Label htmlFor='two-factor-code-0'>Authentication code</Label>
              <CodeInputs aria-label='Authentication code'>
                {Array.from({ length: CODE_LENGTH }).map((_, index) => (
                  <CodeInput
                    key={index}
                    id={index === 0 ? 'two-factor-code-0' : undefined}
                    ref={element => {
                      codeInputRefs.current[index] = element
                    }}
                    value={code[index] || ''}
                    onChange={handleCodeChange(index)}
                    onKeyDown={handleCodeKeyDown(index)}
                    onPaste={handleCodePaste(index)}
                    inputMode='numeric'
                    autoComplete={index === 0 ? 'one-time-code' : 'off'}
                    autoFocus={index === 0}
                    maxLength={CODE_LENGTH}
                  />
                ))}
              </CodeInputs>
              <PrimaryButton type='submit' disabled={code.length !== CODE_LENGTH} data-tour-id='twoFactor.enable-btn'>
                Enable two-factor authentication
              </PrimaryButton>
            </SetupForm>
          </>
        )}

        {step === 'recovery' && (
          <>
            <Eyebrow>Final step</Eyebrow>
            <Title>Save your recovery codes</Title>
            <Description>
              These recovery codes can be used to access your account if you lose access to your authenticator app. Save them now, because you will not be able to see this set again.
            </Description>
            <RecoveryCodes>
              <RecoveryHeader>
                <RecoveryTitle>Recovery codes</RecoveryTitle>
                <RecoveryActions>
                  <SmallButton type='button' onClick={handleCopyRecoveryCodes}>
                    {copiedRecoveryCodes ? 'Copied' : 'Copy all'}
                  </SmallButton>
                  <SmallButton type='button' onClick={handleDownloadRecoveryCodes}>
                    Download .txt
                  </SmallButton>
                </RecoveryActions>
              </RecoveryHeader>
              {recoveryCodes.map(item => (
                <code key={item}>{item}</code>
              ))}
            </RecoveryCodes>
            <PrimaryButton type='button' onClick={continueToApp}>
              Continue
            </PrimaryButton>
          </>
        )}

        {step === 'enabled' && (
          <>
            <Eyebrow>Account security</Eyebrow>
            <Title>Two-factor authentication</Title>
            <EnabledBox>
              <StatusText>Two-factor authentication is enabled.</StatusText>
              <PrimaryButton type='button' onClick={continueToApp}>
                Continue
              </PrimaryButton>
            </EnabledBox>
          </>
        )}
      </Panel>
    </PageWrapper>
  )
}

export default TwoFactorSetup

const PageWrapper = styled.div`
  min-height: ${({ theme }) => `calc(100vh - ${theme.dimensions.topBarHeight})`};
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 48px 16px;
  background: #f7f8fd;
`

const Panel = styled.div`
  width: 100%;
  max-width: 560px;
  background: #ffffff;
  border: 1px solid #e7ebf5;
  border-radius: 8px;
  padding: 36px;
  box-shadow: 0 10px 30px rgba(17, 12, 47, 0.08);

  @media (max-width: 560px) {
    padding: 24px;
  }
`

const Eyebrow = styled.div`
  color: #36becd;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  margin-bottom: 10px;
`

const Title = styled.h1`
  color: #110c2f;
  font-size: 30px;
  line-height: 38px;
  margin: 0 0 12px;
`

const Description = styled.p`
  color: #4b5563;
  line-height: 24px;
  margin: 0 0 24px;
`

const QrWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin: 20px 0;

  svg {
    width: 192px;
    height: 192px;
  }
`

const LoadingBox = styled.div`
  background: #f7f8fd;
  border: 1px solid #e7ebf5;
  border-radius: 8px;
  color: #4b5563;
  margin: 20px 0;
  padding: 18px;
  text-align: center;
`

const ManualKey = styled.div`
  background: #f7f8fd;
  border: 1px solid #e7ebf5;
  border-radius: 8px;
  padding: 14px;
  margin: 0 0 20px;

  span {
    display: block;
    color: #6b7280;
    font-size: 12px;
    margin-bottom: 8px;
  }

  code {
    color: #110c2f;
    word-break: break-all;
  }
`

const Steps = styled.ol`
  color: #374151;
  line-height: 24px;
  padding-left: 20px;
  margin: 0 0 14px;
`

const SetupForm = styled.form`
  display: grid;
  gap: 12px;
`

const Label = styled.label`
  color: #374151;
  font-size: 13px;
  font-weight: 600;
`

const CodeInputs = styled.div`
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 8px;
`

const CodeInput = styled.input`
  width: 100%;
  aspect-ratio: 1;
  border: 1px solid #cdd4ea;
  border-radius: 8px;
  color: #110c2f;
  font-size: 24px;
  font-weight: 700;
  text-align: center;
  outline: none;

  &:focus {
    border-color: #36becd;
    box-shadow: 0 0 0 3px rgba(54, 190, 205, 0.12);
  }
`

const PrimaryButton = styled.button`
  width: 100%;
  min-height: 48px;
  border: 0;
  border-radius: 8px;
  background: #110c2f;
  color: #ffffff;
  font-weight: 700;
  cursor: pointer;
  padding: 0 18px;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`

const SecondaryButton = styled.button`
  min-height: 42px;
  border: 1px solid #cdd4ea;
  border-radius: 8px;
  background: #ffffff;
  color: #110c2f;
  font-weight: 700;
  cursor: pointer;
  padding: 0 14px;

  &:hover {
    border-color: #36becd;
    color: #168996;
  }
`

const ManualSetupButton = styled(SecondaryButton)`
  width: 100%;
  margin: 0 0 16px;
`

const SkipSection = styled.div`
  border-top: 1px solid #e7ebf5;
  margin-top: 24px;
  padding-top: 24px;
`

const SkipTitle = styled.div`
  color: #110c2f;
  font-weight: 700;
  margin-bottom: 6px;
`

const SkipDescription = styled.div`
  color: #4b5563;
  font-size: 13px;
  line-height: 20px;
  margin-bottom: 12px;
`

const SkipGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`

const RecoveryCodes = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
  margin: 0 0 24px;

  code {
    background: #f7f8fd;
    border: 1px solid #e7ebf5;
    border-radius: 6px;
    padding: 8px;
    font-size: 12px;
    word-break: break-all;
  }

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`

const RecoveryHeader = styled.div`
  grid-column: 1 / -1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;

  @media (max-width: 560px) {
    align-items: flex-start;
    flex-direction: column;
  }
`

const RecoveryTitle = styled.div`
  color: #110c2f;
  font-weight: 700;
`

const RecoveryActions = styled.div`
  display: flex;
  gap: 8px;
`

const SmallButton = styled.button`
  background: #ffffff;
  border: 1px solid #cdd4ea;
  border-radius: 6px;
  color: #110c2f;
  cursor: pointer;
  font-size: 12px;
  font-weight: 700;
  height: 34px;
  padding: 0 12px;

  &:hover {
    border-color: #36becd;
    color: #168996;
  }
`

const EnabledBox = styled.div`
  display: grid;
  gap: 20px;
`

const StatusText = styled.div`
  color: #110c2f;
  background: rgba(54, 190, 205, 0.12);
  border: 1px solid rgba(54, 190, 205, 0.35);
  border-radius: 8px;
  padding: 14px;
`
